import type {ReactElement} from 'react';
import {useContext} from 'react';
import {EmailMakerBlockManager} from '@Components/email-maker-block-manager/email-maker-block-manager';
import {useElementsSubPanel, useSelectedBlockPanel} from '@Components/email-maker-block-manager/email-maker-block-manager.hooks';
import {setElementsSubPanel, setSelectedBlockPanel} from '@Components/email-maker-block-manager/email-maker-block-manager-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {requestAiRedesignModalOpen} from '@Components/email-maker-ai-redesign-prompt-box/email-maker-ai-redesign-prompt-box-reducer';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import type {PanelHeaderProps} from '@Components/panel';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EmailMakerBottomSheet} from '@EmailMakerReact/components/email-maker-bottom-sheet';
import {useCloseSheetOnCanvasInteraction} from '@EmailMakerReact/hooks/use-close-sheet-on-canvas-interaction';
import {BLOCK_CATEGORY_TILES} from '@EmailMakerReact/lib/email-maker-tile-meta-library';
import {isAiEditEnabled} from '@EmailMakerReact/lib/email-maker-ai-library';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {BlockSheetHeaderArgs, MountedBlockSheetProps} from './email-maker-block-sheet.types';
import styles from './email-maker-block-sheet.module.scss';

const PANEL_ID = 'email-maker-block-sheet-panel';

export function EmailMakerBlockSheet(): ReactElement | null {
  const {editor} = useContext(emailMakerContext);
  const modalData = useAppSelector((s) => s.modals.modalsHashmap[MODALS_IDS.EMAIL_MAKER_BLOCK_MANAGER_MODAL]);

  if (!modalData?.isOpen || !editor) {
    return null;
  }

  return <MountedBlockSheet editor={editor} />;
}

function MountedBlockSheet({editor}: MountedBlockSheetProps): ReactElement {
  const dispatch = useAppDispatch();
  const selectedCategory = useSelectedBlockPanel();
  const elementsSubPanel = useElementsSubPanel();
  useDefaultPanelClose(PANEL_ID);

  useCloseSheetOnCanvasInteraction(editor, MODALS_IDS.EMAIL_MAKER_BLOCK_MANAGER_MODAL, {closeOnAdd: true});

  const onClose = (): void => {
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_BLOCK_MANAGER_MODAL));
  };

  const onAiRedesign = (): void => {
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_BLOCK_MANAGER_MODAL));
    dispatch(requestAiRedesignModalOpen());
  };

  const onBack = (): void => {
    dispatch(setSelectedBlockPanel(null));
  };

  const onSubPanelBack = (): void => {
    dispatch(setElementsSubPanel(null));
  };

  return (
    <EmailMakerBottomSheet modalId={MODALS_IDS.EMAIL_MAKER_BLOCK_MANAGER_MODAL} panelId={PANEL_ID}>
      <Panel
        panelHeader={renderPanelHeader({selectedCategory, elementsSubPanel, onClose, onBack, onSubPanelBack})}
        panelContent={
          <PanelContent className={styles.body}>
            <EmailMakerBlockManager gjsEditor={editor} getIsAiEditEnabled={(): boolean => isAiEditEnabled(editor)} hideMobileHeader onAiRedesign={onAiRedesign} mobileBreakpoint={ScreenBreakpoints.LARGE} />
          </PanelContent>
        }
      />
    </EmailMakerBottomSheet>
  );
}

/**
 * The sheet owns the single header; the block manager suppresses its own via `hideMobileHeader`.
 * When an Elements sub-panel (Text/Image) is open, the header names that sub-tab and its back
 * button returns to the Elements list rather than the top-level category list.
 */
function renderPanelHeader({selectedCategory, elementsSubPanel, onClose, onBack, onSubPanelBack}: BlockSheetHeaderArgs): ReactElement<PanelHeaderProps> {
  if (selectedCategory === window.PMW.emails.blocks.CATEGORY_ELEMENTS && elementsSubPanel !== null) {
    return <PanelHeader title={resolvePanelTitle(elementsSubPanel)} showBackButton onBackButton={onSubPanelBack} onClose={onClose} />;
  }
  if (selectedCategory !== null) {
    return <PanelHeader title={resolvePanelTitle(selectedCategory)} showBackButton onBackButton={onBack} onClose={onClose} />;
  }
  return <PanelHeader title={window.i18next.t('pmwjs_email_maker_add_elements')} onClose={onClose} />;
}

function resolvePanelTitle(selectedCategory: string | null): string {
  const tile = BLOCK_CATEGORY_TILES.find((entry) => entry.id === selectedCategory);
  if (tile) {
    return window.i18next.t(tile.labelKey);
  }
  return window.i18next.t('pmwjs_email_maker_add_elements');
}
